import { tw } from 'twind';
import { LinkCta } from '@global-ecom/nitro-uds/elements';

import { Maybe } from '__generated__/graphql';
import { useTranslate } from 'hooks/useTranslations';
import { useFeature } from 'hooks/useFeature';
import { ApplePayButton } from 'ui/components/ApplePayButton';
import { GooglePayButton } from 'ui/components/GooglePayButton';
import { PayPalButton } from 'ui/components/PayPalButton';
import TermsAndConditions from 'ui/components/TermsAndConditions';
import TermsAndConditionsBackup from 'ui/components/TermsAndConditionsBackup';
import { AnalyticsEvents, event } from 'utils/analytics';
import { ActiveCartGiftWithPurchases } from 'types/cart';
import { SUPPORTED_COUNTRY } from 'utils/constants';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { getIsMobile } from 'utils/media';
import { useModalsOpenStore } from 'store';

import { AmazonPayV2PayButton } from '../AmazonPayV2Button';
import { AdyenApplePayButton } from '../AdyenApplePayButton';
import { MixedCartErrorMessage } from '../MixedCartErrorMessage';
import { isTherePromosButNotASinglePromoClaimed } from '../GiftWithPurchase/utils';
import { GWPOldDesign } from '../GiftWithPurchase/OldDesign';
import { GWPNewDesign } from '../GiftWithPurchase/NewDesign';

type MiniCartActionsProps = {
  addedToWishlist?: boolean;
  productCount: Maybe<number> | undefined;
  giftWithPurchases: Array<ActiveCartGiftWithPurchases> | undefined;
  mixedCartNotAllowed: boolean | undefined;
  showAmazonPayButton: boolean;
  closeMiniCart: () => void;
};

const showPaymentMethods = (country: SUPPORTED_COUNTRY) =>
  ![SUPPORTED_COUNTRY.US, SUPPORTED_COUNTRY.CA].includes(country);

export const MiniCartActions = ({
  addedToWishlist,
  giftWithPurchases,
  productCount,
  mixedCartNotAllowed,
  showAmazonPayButton,
  closeMiniCart,
}: MiniCartActionsProps) => {
  const t = useTranslate();
  const { country } = useSiteConfig();

  const isGiftWithPurchaseNewDesign = useFeature(
    'GIFT_WITH_PURCHASE_NEW_DESIGN'
  );

  const { setModalOpen } = useModalsOpenStore();

  if (addedToWishlist) {
    return (
      <div data-test-id="minicart-actions">
        <LinkCta
          dataTestId="minicart-wishlist-view"
          label={t('miniWishlistViewWishlist')}
          href="/wishlist"
          key="wishlist"
        />
      </div>
    );
  }

  const GWPCalloutButton = isGiftWithPurchaseNewDesign
    ? GWPNewDesign.CalloutButton
    : GWPOldDesign.CalloutButton;

  return (
    <div data-test-id="minicart-actions" className="grid gap-3 mt-4">
      {isTherePromosButNotASinglePromoClaimed({ giftWithPurchases }) && (
        <GWPCalloutButton
          onClick={() => {
            setModalOpen('GWPModal', true);
            closeMiniCart();
          }}
        />
      )}
      <TermsAndConditionsAndPaymentMethods
        productCount={productCount}
        showPaymentMethods={showPaymentMethods(country)}
        mixedCartNotAllowed={mixedCartNotAllowed}
        showAmazonPayButton={showAmazonPayButton}
      />
    </div>
  );
};

const TermsAndConditionsAndPaymentMethods = (props: {
  productCount: Maybe<number | undefined>;
  showPaymentMethods: boolean;
  mixedCartNotAllowed: boolean | undefined;
  showAmazonPayButton: boolean;
}) => {
  if (props.showPaymentMethods) {
    return (
      <TermsAndConditions
        page="tacOnItemAddedPopup"
        backupContent={<TermsAndConditionsBackup />}
      >
        <ViewCartCheckoutButtons
          productCount={props.productCount}
          mixedCartNotAllowed={props.mixedCartNotAllowed}
        />
        <PayPalButton />
        <ApplePayButton />
        <AdyenApplePayButton />
        <GooglePayButton buttonType="pay" />
        <>
          {props.showAmazonPayButton && (
            <AmazonPayV2PayButton
              style={{
                height: '60px',
                width: '200px',
                justifySelf: 'center',
              }}
            />
          )}
        </>
      </TermsAndConditions>
    );
  }
  return (
    <TermsAndConditions
      page="tacOnItemAddedPopup"
      backupContent={<TermsAndConditionsBackup />}
    >
      <ViewCartCheckoutButtons
        productCount={props.productCount}
        mixedCartNotAllowed={props.mixedCartNotAllowed}
      />
    </TermsAndConditions>
  );
};

const ViewCartCheckoutButtons = (props: {
  productCount: Maybe<number | undefined>;
  mixedCartNotAllowed?: boolean | null;
}) => {
  const t = useTranslate();
  const isMobile = getIsMobile();
  const separateViewAndCheckoutButtons = useFeature(
    'MINICART_SEPARATE_VIEW_AND_CHECKOUT_BUTTONS'
  );

  const cartCount =
    props.productCount === undefined ? 0 : Number(props.productCount);

  const viewCartText = separateViewAndCheckoutButtons
    ? t('miniCartViewCart', { count: cartCount })
    : t('miniCartViewCartAndCheckout', { count: cartCount });

  return (
    <div
      className={tw(
        'grid gap-3 mobile:grid-cols-1',
        separateViewAndCheckoutButtons ? 'grid-cols-2' : 'grid-cols-1'
      )}
    >
      {!isMobile && (
        <MixedCartErrorMessage
          mixedCartNotAllowed={props.mixedCartNotAllowed!}
        />
      )}

      <LinkCta
        dataTestId="minicart-cart-link"
        label={viewCartText}
        href="/cart"
        key="cart"
        className="flex-grow"
        variant={separateViewAndCheckoutButtons ? 'secondary' : 'primary'}
        size="lg"
        onClick={() => {
          event(AnalyticsEvents.MINICART, {
            domevent: 'button clicked',
            domlabel: 'cart page',
          });
          event(AnalyticsEvents.GA4_CustomEvent, {
            event_name: AnalyticsEvents.MINICART,
            event_params: {
              user_action: 'view cart/checkout',
            },
          });
        }}
      />
      {isMobile && (
        <MixedCartErrorMessage
          mixedCartNotAllowed={props.mixedCartNotAllowed!}
        />
      )}
      {separateViewAndCheckoutButtons && (
        <LinkCta
          dataTestId="minicart-checkout-link"
          label={t('checkout')}
          href="/checkout"
          className="flex-grow"
          key="checkout"
          variant="primary"
          size="lg"
          dimmed={props.mixedCartNotAllowed as boolean}
          onClick={e => {
            if (props.mixedCartNotAllowed) {
              e.preventDefault();
              return false;
            }
            event(AnalyticsEvents.MINICART, {
              domevent: 'button clicked',
              domlabel: 'checkout',
            });
            event(AnalyticsEvents.GA4_CustomEvent, {
              event_name: AnalyticsEvents.MINICART,
              event_params: {
                user_action: 'view cart/checkout',
              },
            });
          }}
        />
      )}
    </div>
  );
};
